import type { JSX, PropsWithChildren, ReactElement } from "react"

import {
	AccessErrorMessage,
	getErrorMessageFromObjects,
	type SecurityObject,
	UserAccessRestrictionCode, useUserInfo
} from "~/domain/user-session"
import { Loading } from "~/shared/ui"

import { useUserAccessRestrictionStatus } from "../hooks"

export type SecurityObjectExt = SecurityObject & {
	errorMessage?: string | JSX.Element
}

/**
 * Свойства {@link RestrictedAccess}
 */
export interface RestrictedAccessProps {
	/** Объекты (или объект), к которым у пользователя должен быть доступ */
	objects?: string | SecurityObjectExt | SecurityObjectExt[]
	/** Игнорировать неизмененный пароль  */
	ignoreNotChangedPassword?: boolean
	/** Тип проверки: "and" - доступ должен быть ко всем объектам; "or" - доступ должен быть хотя бы к одному */
	secureOp?: "and" | "or"
	/** Функция рендер, используемая при ошибке доступа вместо вывода дочерних элементов */
	fallbackRender?: (
		code: number,
		errorMessage?: string | ReactElement
	) => ReactElement | string | null
	defErrorMessage?: string | ReactElement
}

const defFallbackRender = (code, errorMessage) => (
	<AccessErrorMessage code={code} errorMessage={errorMessage} />
)

/**
 * Используется для ограничения доступа к контенту
 * @param props
 * @component
 * @category Component
 * @example
 *```tsx
 * import React from "react";
 * import {Restricted} from "~/features/security";
 * import {useFacultyInfo} from "~/features/dekanat";
 *
 * function Example1() {
 *
 *     return (
 *         <RestrictedAccess>
 *             <div>Защищенный контент</div>
 *         </RestrictedAccess>
 *     )
 * }
 *
 * function Example2() {
 *
 *     const facultyInfo = useFacultyInfo();
 *
 *     return (
 *         <RestrictedAccess objects={{
 *             object: 'iais:dekanat',
 *             restriction: {name: "facultyId", value: facultyInfo?.id},
 *             errorMessage: facultyInfo?.name ? `У вас нет доступа к выбранному ${facultyInfo.isFaculty ? 'факультету' : 'институту'} (${facultyInfo.name})!` : ''
 *         }}>
 *             <div>Защищенный контент</div>
 *         </RestrictedAccess>
 *     )
 * }
 * ```
 */
export function RestrictedAccess({
									 children,
									 objects,
									 ignoreNotChangedPassword,
									 secureOp,
									 defErrorMessage,
									 fallbackRender = defFallbackRender
								 }: PropsWithChildren<RestrictedAccessProps>): ReactElement {
	const { code, objectIndex, isPending } = useUserAccessRestrictionStatus({
		objects,
		ignoreNotChangedPassword,
		secureOp
	})

	if (isPending) {
		return <Loading loading delay={0.5} title="Проверка доступа..." />
	}

	const errorMessage = getErrorMessageFromObjects(objects, objectIndex, defErrorMessage)

	if (code === UserAccessRestrictionCode.ACCESS_IS_ALLOWED) {
		return <>{children}</>
	} else {
		return <>{fallbackRender?.(code, errorMessage)}</>
	}
}

export function EmployeeAccess({ children, fallbackRender = defFallbackRender, ...props } : PropsWithChildren<RestrictedAccessProps>) {

	const userInfo = useUserInfo()

	if(userInfo?.userType !== "сотрудник" && userInfo?.userType !== "клиент") return <>{fallbackRender?.(UserAccessRestrictionCode.NO_ACCESS, "Доступ разрешен только сотрудникам, прошедшим аутентификацию")}</>

	return <RestrictedAccess {...props}>{children}</RestrictedAccess>
}

export function StudentAccess({ children, fallbackRender = defFallbackRender, ...props } : PropsWithChildren<RestrictedAccessProps>) {

	const userInfo = useUserInfo()

	if(userInfo?.userType !== "обучающийся") return <>{fallbackRender?.(UserAccessRestrictionCode.NO_ACCESS, "Доступ разрешен только студентам, прошедшим аутентификацию")}</>

	return <RestrictedAccess {...props}>{children}</RestrictedAccess>
}